import React from 'react';
import moment from 'moment-timezone';
import {
  Timeline,
  TimelineChart,
  TimelineRulerTrack,
  TimelineTrack,
  TimelineTrackLabel,
  TimelineTrackSlot,
  TimelineTrackColor,
  TimelineWeatherConditions,
  TimelineMarkers,
  TimelineVideoThumbnails,
} from 'components/timeline';
import { useJobsitePulse } from './JobsitePulseProvider';
import { timelinePanelStyles } from './styles';
import { ActivityFlag } from './ActivityFlag';

export function TimelinePanel(): React.ReactElement {
  const { activityList, timeSeries, selectedCamera, weatherConditions } =
    useJobsitePulse();

  const weatherData = weatherConditions ?? [];

  const workersOnSiteData = React.useMemo(() => {
    return (
      timeSeries?.sessions
        .filter(({ dateTime }) => {
          const hourOfDay = moment(dateTime).hour();
          // Filter for hours between 6 AM and 5 PM,
          return hourOfDay >= 6 && hourOfDay <= 17;
        })
        .map(({ dateTime, value }) => ({
          label: moment(dateTime).format('hh:mm A'),
          value,
        })) ?? []
    );
  }, [timeSeries?.sessions]);

  const documentMarkers = React.useMemo(() => {
    return (
      activityList?.map((activity) => ({
        icon: (
          <ActivityFlag
            type={activity.isIncident ? 'danger' : 'default'}
            text={activity?.label}
          />
        ),
        time: moment(`${activity.date} ${activity.time}`).toDate().getTime(),
      })) ?? []
    );
  }, [activityList]);

  const thumbnails = React.useMemo(
    () => selectedCamera?.thumbnails ?? [],
    [selectedCamera],
  );

  // TODO: [sc-13382] Replace mock weather data with real API data
  const tracks: {
    label: string;
    color: TimelineTrackColor;
    children: React.ReactNode;
  }[] = [
    {
      label: 'Weather',
      color: 'neutral',
      children: <TimelineWeatherConditions weather={weatherData} />,
    },
    {
      label: 'Logs',
      color: 'neutral',
      children: <TimelineMarkers items={documentMarkers} />,
    },
    {
      label: 'Workforce',
      color: 'brand',
      children: <TimelineChart data={workersOnSiteData} />,
    },
    {
      label: 'Cameras',
      color: 'info',
      children: <TimelineVideoThumbnails items={thumbnails} />,
    },
  ];

  return (
    <Timeline className={timelinePanelStyles.root}>
      <TimelineRulerTrack hideScaleSlider />
      {tracks.map(({ children, color, label }) => (
        <TimelineTrack key={label}>
          <TimelineTrackLabel text={label} />
          <TimelineTrackSlot color={color}>{children}</TimelineTrackSlot>
        </TimelineTrack>
      ))}
    </Timeline>
  );
}
